// All consts
const ZERO = 0;
const ONE = 1;
const TWO = 2;
const ONEOTHREE = 1 / 3;

export const Underlying = {
  ClosePrice: Symbol('Underlying.ClosePrice'),
  TypicalPrice: Symbol('Underlying.TypicalPrice')
};

const MovingAverage = {
  Arithmetic: Symbol('MovingAverage.Arithmetic'),
  Exponential: Symbol('MovingAverage.Exponential'),
  Wilders: Symbol('MovingAverage.Wilders')
};

const MovingDeviation = {
  Mean: Symbol('MovingDeviation.Mean'),
  Standard: Symbol('MovingDeviation.Standard')
};

/*
const Coefficient = {
  OpenPrices: Symbol('Coefficient.OpenPrices'),
  ClosePrices: Symbol('Coefficient.ClosePrices'),
  ClosePricesWithoutAggregation: Symbol('Coefficient.ClosePricesWithoutAggregation')
};
*/

const arithmeticMovingAverage = (ts, m) => {
  m = Math.min(m, ts.length);
  const oneom = ONE / m;
  let sum = ts[0];

  return ts.map(function(x, i) {
    if (!i) {
      return x;
    } else if (i < m) {
      sum += x;
      return sum / (i + ONE);
    } else {
      sum += x - ts[i - m];
      return sum * oneom;
    }
  });
};

const exponentialMovingAverage = (ts, m) => {
  m = Math.min(m, ts.length);
  const twoompone = TWO / (m + ONE);
  const onemtwoompone = ONE - twoompone;

  return ts.reduce(function(all, x, i) {
    if (!i) {
      all.push(x);
    } else if (i < m) {
      all.push(all[i - 1] + (TWO * (x - all[i - 1])) / (i + TWO));
    } else {
      all.push(all[i - 1] * onemtwoompone + x * twoompone);
    }

    return all;
  }, []);
};

const wildersMovingAverage = (ts, m) => {
  m = Math.min(m, ts.length);
  const oneom = ONE / m;
  const onemoneom = ONE - oneom;

  return ts.reduce(function(all, x, i) {
    if (!i) {
      all.push(x);
    } else if (i < m) {
      all.push(all[i - 1] + (x - all[i - 1]) / (i + ONE));
    } else {
      all.push(all[i - 1] * onemoneom + x * oneom);
    }

    return all;
  }, []);
};

function lr(ts) {
  return ts.map(function(x, i) {
    return !i ? ZERO : Math.log(x / ts[i - 1]);
  });
}

function movul(hts, lts, cts, ul) {
  if ([Underlying.ClosePrice, Underlying.TypicalPrice].indexOf(ul) === -1) {
    throw new Error('underlying type ' + ul + ' does not exist');
  }
  return ul === Underlying.ClosePrice ? cts : tp(hts, lts, cts);
}

function tp(hts, lts, cts) {
  return cts.map(function(_, i) {
    return (hts[i] + lts[i] + cts[i]) * ONEOTHREE;
  });
}

function movdev(ts, md, m) {
  m = Math.min(m, ts.length);
  const movarmean = movmean(ts, MovingAverage.Arithmetic, m);

  if (md === MovingDeviation.Mean) {
    const oneom = ONE / m;

    return ts.map(function(_, i) {
      if (!i) {
        return ZERO;
      } else if (i < m) {
        let sum = ZERO;
        for (let j = 0; j < i; j++) {
          sum += Math.abs(movarmean[i] - ts[j]);
        }
        return sum / (i + ONE);
      } else {
        let sum = ZERO;
        for (let j = 0; j < m; j++) {
          sum += Math.abs(movarmean[i] - ts[i - j]);
        }
        return sum * oneom;
      }
    });
  }

  if (md === MovingDeviation.Standard) {
    return movmean(
      ts.map(function(x) {
        return x * x;
      }),
      MovingAverage.Arithmetic,
      m
    ).reduce(function(all, x, i) {
      all.push(Math.sqrt(x - movarmean[i] * movarmean[i]));
      return all;
    }, []);
  }

  throw new Error('movingDeviation type ' + md + ' does not exist');
}

function movmean(ts, ma, m) {
  switch (ma) {
    case MovingAverage.Arithmetic:
      return arithmeticMovingAverage(ts, m);
    case MovingAverage.Exponential:
      return exponentialMovingAverage(ts, m);
    case MovingAverage.Wilders:
      return wildersMovingAverage(ts, m);
    default:
      throw new Error('movingAverage type ' + ma + ' does not exist');
  }
}

/*
 * nocpn = number of observations in one year / annualization factor to get annualized historical volatility (defaults to 252 for daily obs, 52 for weekly obs, 12 for monthly obs),
 *         will be used as sqrt(nocpn), e.g. sqrt(252) inside the function
 */
function calculateHistoricalVolatility(hts, lts, cts, ul, nocp1, nocp2, nocpn) {
  const norm = Math.sqrt(nocpn);
  const logReturn = lr(movul(hts, lts, cts, ul));

  const hv1 = movdev(logReturn, MovingDeviation.Standard, nocp1).map(function(x) {
    return x * norm * 100;
  });
  /*
    var hv2 = movdev(logReturn, MovingDeviation.Standard, nocp2).map(function(x) {
      return x * norm;
    });
    */
  return hv1;
}

function getImpliedVolatility(va, vb) {
  let implVola;

  if (va == 0) {
    implVola = vb;
  } else if (vb == 0) {
    implVola = va;
  } else {
    implVola = (parseFloat(va) + parseFloat(vb)) / 2;
  }

  return parseFloat(implVola);
}

export const volatilityCalculations = {
  Underlying,
  calculateHistoricalVolatility,
  getImpliedVolatility
};
